import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { groupBy, kebabCase, uniqBy } from "lodash";
import { moveColumn } from "../utils/moveColumn";
import { SET_PREFERENCES } from "../../../profile/redux/actionTypes";
import { Preferences } from "../../../profile/objects/preferences";
import { Column } from "../objects/column";
import { ListOrderFilter } from "../objects/listOrderFilter";

type State = {
    listColumns: {
        [index: string]: {
            preferences: Preferences[],
            columns: Column[],
            order: ListOrderFilter
        }
    },
}

const initialState: State = {
    listColumns: {
        'MAIL_TEMPLATE': {
            preferences: [],
            columns: [{
                label: 'name',
                field_name: 'NAME',
                checked: true,
                order_by: 'name',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'subject',
                field_name: 'SUBJECT',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'action',
                field_name: 'ACTION',
                checked: true,
                order_by: 'action',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'replyable',
                field_name: 'REPLYABLE',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'activated',
                field_name: 'ACTIVATED',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'display-in-apps',
                field_name: 'DISPLAY_IN_APPS',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'created-date',
                field_name: 'CREATED_DATE',
                checked: true,
                order_by: 'created_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-by',
                field_name: 'CREATED_BY',
                checked: true,
                order_by: 'creator__username',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-date',
                field_name: 'MODIFIED_DATE',
                checked: true,
                order_by: 'modified_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-by',
                field_name: 'MODIFIED_BY',
                checked: true,
                order_by: 'last_author__username',
                fixed_width: true,
                last_updated: false
            }],
            order: {
                by: null,
                direction: 'asc'
            }
        },
        'NEWSLETTER_CONTACTS': {
            preferences: [],
            columns: [{
                label: 'first-name',
                field_name: 'FIRST_NAME',
                checked: true,
                order_by: 'first_name',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'last-name',
                field_name: 'LAST_NAME',
                checked: true,
                order_by: 'last_name',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'email',
                field_name: 'EMAIL',
                checked: true,
                order_by: 'email',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'created-date',
                field_name: 'CREATED_DATE',
                checked: true,
                order_by: 'created_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-by',
                field_name: 'CREATED_BY',
                checked: true,
                order_by: 'creator__username',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-date',
                field_name: 'MODIFIED_DATE',
                checked: true,
                order_by: 'modified_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-by',
                field_name: 'MODIFIED_BY',
                checked: true,
                order_by: 'last_author__username',
                fixed_width: true,
                last_updated: false
            }],
            order: {
                by: null,
                direction: 'asc'
            }
        },
        'NEWSLETTER_CONTACTS_GROUPS': {
            preferences: [],
            columns: [{
                label: 'name',
                field_name: 'name',
                checked: true,
                order_by: 'first_name',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'created-date',
                field_name: 'CREATED_DATE',
                checked: true,
                order_by: 'created_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-by',
                field_name: 'CREATED_BY',
                checked: true,
                order_by: 'creator__username',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-date',
                field_name: 'MODIFIED_DATE',
                checked: true,
                order_by: 'modified_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-by',
                field_name: 'MODIFIED_BY',
                checked: true,
                order_by: 'last_author__username',
                fixed_width: true,
                last_updated: false
            }],
            order: {
                by: null,
                direction: 'asc'
            }
        },
        'NEWSLETTERS': {
            preferences: [],
            columns: [{
                label: 'name',
                field_name: 'NAME',
                checked: true,
                order_by: 'name',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'subject',
                field_name: 'SUBJECT',
                checked: true,
                order_by: 'subject',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'sender-name',
                field_name: 'SENDER_NAME',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'sender-mail',
                field_name: 'SENDER_MAIL',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'is-sent',
                field_name: 'IS_SENT',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'sending-date',
                field_name: 'SENDING_DATE',
                checked: true,
                order_by: 'sending_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-date',
                field_name: 'CREATED_DATE',
                checked: true,
                order_by: 'created_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-by',
                field_name: 'CREATED_BY',
                checked: true,
                order_by: 'creator__username',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-date',
                field_name: 'MODIFIED_DATE',
                checked: true,
                order_by: 'modified_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-by',
                field_name: 'MODIFIED_BY',
                checked: true,
                order_by: 'last_author__username',
                fixed_width: true,
                last_updated: false
            }],
            order: {
                by: null,
                direction: 'asc'
            }
        },
        'CIRCUIT_PRODUCTS': {
            preferences: [],
            columns: [{
                label: 'product-type',
                field_name: 'PRODUCT_TYPE',
                checked: true,
                order_by: 'product_type',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'name',
                field_name: 'NAME',
                checked: true,
                order_by: 'name',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'description',
                field_name: 'DESCRIPTION',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'version',
                field_name: 'VERSION',
                checked: true,
                order_by: 'version',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'variant',
                field_name: 'VARIANT',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'optional',
                field_name: 'OPTIONAL',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'locked',
                field_name: 'LOCKED',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'included-in-the-price',
                field_name: 'INCLUDED_IN_THE_PRICE',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-date',
                field_name: 'CREATED_DATE',
                checked: true,
                order_by: 'created_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-by',
                field_name: 'CREATED_BY',
                checked: true,
                order_by: 'creator__username',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-date',
                field_name: 'MODIFIED_DATE',
                checked: true,
                order_by: 'modified_date',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-by',
                field_name: 'MODIFIED_BY',
                checked: true,
                order_by: 'last_author__username',
                fixed_width: true,
                last_updated: false
            }],
            order: {
                by: null,
                direction: 'asc'
            }
        },
        'CIRCUIT_CANCELATION_RULES': {
            preferences: [],
            columns: [ {
                label: 'name',
                field_name: 'NAME',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'created-date',
                field_name: 'CREATED_DATE',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-date',
                field_name: 'MODIFIED_DATE',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-by',
                field_name: 'CREATED_BY',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'modified-by',
                field_name: 'MODIFIED_BY',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }],
            order: {
                by: null,
                direction: 'asc'
            }
        },
        'CIRCUIT_GLOBAL_CANCELATION_RULES': {
            preferences: [],
            columns: [ {
                label: 'name',
                field_name: 'NAME',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'created-date',
                field_name: 'CREATED_DATE',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'modified-date',
                field_name: 'MODIFIED_DATE',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }, {
                label: 'created-by',
                field_name: 'CREATED_BY',
                checked: true,
                order_by: '',
                fixed_width: false,
                last_updated: false
            }, {
                label: 'modified-by',
                field_name: 'MODIFIED_BY',
                checked: true,
                order_by: '',
                fixed_width: true,
                last_updated: false
            }],
            order: {
                by: null,
                direction: 'asc'
            }
        }
    }
};

const slice = createSlice({
    name: 'lists',
    initialState,
    reducers: {
        addPreferences: (
            state,
            action: PayloadAction<{
                listName: string,
                preferences: Preferences[]
            }>
        ): void => {
            const list = state.listColumns[action.payload.listName];
            state.listColumns[action.payload.listName] = {
                columns: list?.columns ?? [],
                order: list?.order ?? { by: null, direction: 'asc' },
                preferences: (list?.preferences ?? []).concat(action.payload.preferences)
            };
        },
        moveDownColumn: (
            state,
            action: PayloadAction<{
                listName: string,
                index: number,
            }>
        ): void => {
            const list = state.listColumns[action.payload.listName];
            state.listColumns[action.payload.listName] = {
                preferences: list?.preferences ?? [],
                order: list?.order ?? { by: null, direction: 'asc' },
                columns: moveColumn(list?.columns ?? [], action.payload.index, action.payload.index + 1),
            };
        },
        moveUpColumn: (
            state,
            action: PayloadAction<{
                listName: string,
                index: number,
            }>
        ): void => {
            const list = state.listColumns[action.payload.listName];
            state.listColumns[action.payload.listName] = {
                preferences: list?.preferences ?? [],
                order: list?.order ?? { by: null, direction: 'asc' },
                columns: moveColumn(list?.columns ?? [], action.payload.index, action.payload.index - 1),
            };
        },
        setPreferences: (
            state,
            action: PayloadAction<{
                listName: string,
                preferences: Preferences[]
            }>
        ): void => {
            const list = state.listColumns[action.payload.listName];
            state.listColumns[action.payload.listName] = {
                columns: list?.columns ?? [],
                order: list?.order ?? { by: null, direction: 'asc' },
                preferences: action.payload.preferences
            };
        },
        setOrder: (
            state,
            action: PayloadAction<{
                listName: string,
                order: ListOrderFilter
            }>
        ): void => {
            const list = state.listColumns[action.payload.listName];
            state.listColumns[action.payload.listName] = {
                preferences: list?.preferences ?? [],
                columns: list?.columns ?? [],
                order: action.payload.order
            };
        },
        toggleColumn: (
            state,
            action: PayloadAction<{
                listName: string,
                index: number,
            }>
        ): void => {
            const list = state.listColumns[action.payload.listName];
            const column = list?.columns[action.payload.index];
            if (column) {
                column.checked = !column.checked;
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase<string, PayloadAction<Preferences[]>>(SET_PREFERENCES, (state, action) => {
            const preferences = groupBy(action.payload, 'module');
            const modules = Object.keys(preferences);

            for (const module of modules) {
                const listPreferences = preferences[module] ?? [];

                const preferenceWithOrdering = listPreferences.find((preference) => preference.ordering_asc !== null);
                const orderBy =  preferenceWithOrdering ? preferenceWithOrdering.field_name.toLowerCase() : '-created_date';
                const orderDirection: 'asc' | 'desc' = preferenceWithOrdering?.ordering_asc ? 'asc' : 'desc';

                state.listColumns[module] = {
                    columns: state.listColumns[module]?.columns ?? [],
                    order: {
                        by: preferenceWithOrdering ? orderBy : null,
                        direction: orderDirection
                    },
                    preferences: listPreferences
                };

                const newColumns = uniqBy(listPreferences, 'field_name').map((preference): Column => {
                    return {
                        label: kebabCase(preference.field_name),
                        field_name: preference.field_name,
                        checked: preference.active,
                        order_by: initialState.listColumns[module]?.columns.find((column) => column.field_name === preference.field_name)?.order_by ?? '',
                        fixed_width: false,
                        last_updated: false
                    };
                });
                const currentColumns = state.listColumns[module]?.columns.filter((column) => {
                    return newColumns.findIndex((item) => item.field_name === column.field_name) < 0;
                }) ?? [];

                const list = state.listColumns[module];
                if (list) {
                    list.columns = newColumns.concat(currentColumns);
                }
            }
        });
    }
});

export const {
    addPreferences,
    moveDownColumn,
    moveUpColumn,
    setPreferences,
    setOrder,
    toggleColumn,
} = slice.actions;

export default slice;